import React, { useState } from "react";
import { Row, Col, Form, Button, Spinner, Modal } from "react-bootstrap";
import { FaPaw, FaPlus } from "react-icons/fa";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import axios from "axios";

const PetsTab = ({ pets, breeds, customerId, loading, onPetsUpdate, onShowSuccess, onShowError }) => {
    const [editingPetId, setEditingPetId] = useState(null);
    const [savingPet, setSavingPet] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingPet, setCreatingPet] = useState(false);

    const [petFormData, setPetFormData] = useState({});
    const [newPetFormData, setNewPetFormData] = useState({
        name: '',
        breed_id: '',
        age: '',
        color: '',
        male: '',
        is_dead: false,
        birth_date: '',
        death_date: '',
        weight: '',
    });

    const handleEnablePetEdit = (pet) => {
        setEditingPetId(pet.id);
        setPetFormData({
            name: pet.name || '',
            breed_id: pet.breed_id || '',
            age: pet.age || '',
            color: pet.color || '',
            male: pet.male !== undefined ? pet.male.toString() : '',
            is_dead: pet.is_dead === 1 || pet.is_dead === true,
            birth_date: pet.birth_date || '',
            death_date: pet.death_date || '',
            weight: pet.weight || '',
        });
    };

    const handleCancelPetEdit = () => {
        setEditingPetId(null);
        setPetFormData({});
    };

    const handlePetFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPetFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNewPetFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewPetFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleUpdatePet = async (petId) => {
        try {
            setSavingPet(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = {
                name: petFormData.name,
                breed_id: petFormData.breed_id,
                age: petFormData.age,
                color: petFormData.color,
                male: petFormData.male === '1' || petFormData.male === true,
                is_dead: petFormData.is_dead,
                birth_date: petFormData.birth_date,
                death_date: petFormData.death_date,
                weight: petFormData.weight,
                customer_id: customerId
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets-update/${petId}`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Pet updated successfully");
                setEditingPetId(null);
                setPetFormData({});
                onPetsUpdate();
            } else {
                onShowError(response.data.message || "Failed to update pet");
            }
        } catch (err) {
            console.error("Error updating pet:", err);
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = Object.values(errors).flat().join(', ');
                onShowError(errorMessages);
            } else {
                onShowError(err.response?.data?.message || "Failed to update pet");
            }
        } finally {
            setSavingPet(false);
        }
    };

    const handleCreatePet = async () => {
        try {
            setCreatingPet(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = {
                name: newPetFormData.name,
                breed_id: newPetFormData.breed_id,
                age: newPetFormData.age,
                color: newPetFormData.color,
                male: newPetFormData.male === '1' || newPetFormData.male === true,
                is_dead: newPetFormData.is_dead,
                birth_date: newPetFormData.birth_date,
                death_date: newPetFormData.death_date,
                weight: newPetFormData.weight,
                customer_id: customerId
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Pet created successfully");
                setShowCreateModal(false);
                setNewPetFormData({
                    name: '',
                    breed_id: '',
                    age: '',
                    color: '',
                    male: '',
                    is_dead: false,
                    birth_date: '',
                    death_date: '',
                    weight: '',
                });
                onPetsUpdate();
            } else {
                onShowError(response.data.message || "Failed to create pet");
            }
        } catch (err) {
            console.error("Error creating pet:", err);
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = Object.values(errors).flat().join(', ');
                onShowError(errorMessages);
            } else {
                onShowError(err.response?.data?.message || "Failed to create pet");
            }
        } finally {
            setCreatingPet(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const formatGender = (male) => {
        if (male === 1 || male === true) return 'Male';
        if (male === 0 || male === false) return 'Female';
        return 'N/A';
    };

    const formatBreed = (breedId) => {
        const findBreed = (breedsList, id) => {
            for (let breed of breedsList) {
                if (breed.id === id) return breed.name;
                if (breed.child_breeds && breed.child_breeds.length > 0) {
                    const found = findBreed(breed.child_breeds, id);
                    if (found) return found;
                }
            }
            return null;
        };
        return findBreed(breeds, breedId) || 'N/A';
    };

    const renderBreedsOptions = (breedsList, level = 0) => {
        let options = [];
        breedsList.forEach(breed => {
            options.push(
                <option key={breed.id} value={breed.id} style={{ paddingLeft: `${level * 20}px` }}>
                    {'\u00A0'.repeat(level * 4)}{breed.name}
                </option>
            );
            if (breed.child_breeds && breed.child_breeds.length > 0) {
                options.push(...renderBreedsOptions(breed.child_breeds, level + 1));
            }
        });
        return options;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading pets...</p>
            </div>
        );
    }

    return (
        <div className="mt-4">
            {/* Add New Pet Button */}
            <div className="mb-4 text-end">
                <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                    className="px-4"
                >
                    <FaPlus className="me-2" /> Add New Pet
                </Button>
            </div>

            {pets.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted">No pets found for this customer.</p>
                </div>
            ) : (
                <div>
                    {pets.map((pet) => (
                        <div key={pet.id} className="mc-card mb-4 p-4">
                            {/* Pet Header */}
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center me-3"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: '#e2e8f0'
                                        }}
                                    >
                                        <FaPaw size={24} color="#4a5568" />
                                    </div>
                                    <div>
                                        <h4 className="mb-0">{pet.name}</h4>
                                    </div>
                                </div>
                                {editingPetId !== pet.id && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleEnablePetEdit(pet)}
                                    >
                                        <FiEdit size={14} className="me-1" /> Edit Pet
                                    </Button>
                                )}
                            </div>

                            {/* Pet Information Fields */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            <FaPaw className="me-2" size={14} /> Pet Name
                                        </Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={petFormData.name}
                                                onChange={handlePetFieldChange}
                                                placeholder="Enter pet name"
                                            />
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={pet.name || 'N/A'}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Breed</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Select
                                                name="breed_id"
                                                value={petFormData.breed_id}
                                                onChange={handlePetFieldChange}
                                            >
                                                <option value="">Select Breed</option>
                                                {renderBreedsOptions(breeds)}
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={formatBreed(pet.breed_id)}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Gender</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Select
                                                name="male"
                                                value={petFormData.male}
                                                onChange={handlePetFieldChange}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="1">Male</option>
                                                <option value="0">Female</option>
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={formatGender(pet.male)}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Color</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Control
                                                type="text"
                                                name="color"
                                                value={petFormData.color}
                                                onChange={handlePetFieldChange}
                                                placeholder="Enter color"
                                            />
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={pet.color || 'N/A'}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Age (years)</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Control
                                                type="text"
                                                name="age"
                                                value={petFormData.age}
                                                onChange={handlePetFieldChange}
                                                placeholder="Enter age"
                                            />
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={pet.age || 'N/A'}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Weight (kg)</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Control
                                                type="text"
                                                name="weight"
                                                value={petFormData.weight}
                                                onChange={handlePetFieldChange}
                                                placeholder="Enter weight"
                                            />
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={pet.weight || 'N/A'}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Birth Date</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Control
                                                type="date"
                                                name="birth_date"
                                                value={petFormData.birth_date}
                                                onChange={handlePetFieldChange}
                                            />
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={formatDate(pet.birth_date)}
                                                disabled
                                                readOnly
                                                className="bg-light"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Status</Form.Label>
                                        {editingPetId === pet.id ? (
                                            <Form.Select
                                                name="is_dead"
                                                value={petFormData.is_dead}
                                                onChange={handlePetFieldChange}
                                            >
                                                <option value={false}>Alive</option>
                                                <option value={true}>Deceased</option>
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                value={pet.is_dead ? 'Deceased' : 'Alive'}
                                                disabled
                                                readOnly
                                                className={`bg-light ${pet.is_dead ? 'text-danger' : 'text-success'}`}
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {((editingPetId === pet.id && petFormData.is_dead) || (!editingPetId && pet.is_dead)) && (
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">Death Date</Form.Label>
                                            {editingPetId === pet.id ? (
                                                <Form.Control
                                                    type="date"
                                                    name="death_date"
                                                    value={petFormData.death_date}
                                                    onChange={handlePetFieldChange}
                                                />
                                            ) : (
                                                <Form.Control
                                                    type="text"
                                                    value={formatDate(pet.death_date)}
                                                    disabled
                                                    readOnly
                                                    className="bg-light"
                                                />
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}

                            {/* Action Buttons when editing */}
                            {editingPetId === pet.id && (
                                <Row className="mt-3">
                                    <Col md={12} className="text-end">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleCancelPetEdit}
                                            className="me-2"
                                            disabled={savingPet}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleUpdatePet(pet.id)}
                                            disabled={savingPet}
                                        >
                                            {savingPet ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create Pet Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Pet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Pet Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={newPetFormData.name}
                                    onChange={handleNewPetFieldChange}
                                    placeholder="Enter pet name"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Breed</Form.Label>
                                <Form.Select
                                    name="breed_id"
                                    value={newPetFormData.breed_id}
                                    onChange={handleNewPetFieldChange}
                                >
                                    <option value="">Select Breed</option>
                                    {renderBreedsOptions(breeds)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Gender</Form.Label>
                                <Form.Select
                                    name="male"
                                    value={newPetFormData.male}
                                    onChange={handleNewPetFieldChange}
                                >
                                    <option value="">Select Gender</option>
                                    <option value="1">Male</option>
                                    <option value="0">Female</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Color</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={newPetFormData.color}
                                    onChange={handleNewPetFieldChange}
                                    placeholder="Enter color"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Age (years)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="age"
                                    value={newPetFormData.age}
                                    onChange={handleNewPetFieldChange}
                                    placeholder="Enter age"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Weight (kg)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="weight"
                                    value={newPetFormData.weight}
                                    onChange={handleNewPetFieldChange}
                                    placeholder="Enter weight"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Birth Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="birth_date"
                                    value={newPetFormData.birth_date}
                                    onChange={handleNewPetFieldChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Status</Form.Label>
                                <Form.Select
                                    name="is_dead"
                                    value={newPetFormData.is_dead}
                                    onChange={handleNewPetFieldChange}
                                >
                                    <option value={false}>Alive</option>
                                    <option value={true}>Deceased</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {newPetFormData.is_dead && (
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Death Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="death_date"
                                        value={newPetFormData.death_date}
                                        onChange={handleNewPetFieldChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreatePet} disabled={creatingPet}>
                        {creatingPet ? 'Creating...' : 'Create Pet'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PetsTab;